/**
 * SitePick front — 「自社の情報で見る」のメモ（プレーンテキスト。docs/22 §3.8）。
 *
 * 並び: 商品名と URL → ロゴ画像 → 会社名 → 色 → 写真 → 文章。
 * 画像のデータ・ファイル名・内部の ref は書かない。文章は「パーツ名 ／ 元の先頭 12 字… → 新しい文」。
 * 文言は PHP（sitepick_customize_copy()）から受け取る（ここに日本語の文言を持たない）。
 */

/**
 * `%d` / `%s` / `%1$d` の差し込み。
 *
 * @param {string} template
 * @param {...(string|number)} args
 * @returns {string}
 */
export function fmt(template, ...args) {
  let i = 0;
  return String(template || "").replace(/%(?:(\d)\$)?[ds]/g, (_, pos) => {
    const v = pos ? args[Number(pos) - 1] : args[i++];
    return v === undefined ? "" : String(v);
  });
}

/**
 * 文の先頭 12 字（長ければ「…」）。
 *
 * @param {string[]} rows
 * @returns {string}
 */
export function headOf(rows) {
  const t = (rows || []).join(" ").replace(/\s+/g, " ").trim();
  return t.length > 12 ? `${t.slice(0, 12)}…` : t;
}

/**
 * @param {Object} p
 * @param {(key: string) => string} p.msg 文言
 * @param {string} p.title 商品名
 * @param {string} p.url 実物確認画面の URL（クエリ・ハッシュなし）
 * @param {Object} p.data customize-state の data
 * @param {Object|null} p.manifest 検査済みの manifest
 * @param {Object<string, string[]>} p.origTexts 元の文（ref -> 行）
 * @param {string} p.colorLabel 色の案の名前（標準なら空）
 * @param {(ref: string) => string} p.sectionLabel パーツ名
 * @returns {string}
 */
export function buildMemo({ msg, title, url, data, manifest, origTexts, colorLabel, sectionLabel }) {
  const none = msg("memo_none");
  const lines = [msg("memo_title"), fmt(msg("memo_product"), title), fmt(msg("memo_url"), url), ""];

  lines.push(fmt(msg("memo_logo"), data.logo ? fmt(msg("memo_logo_on"), data.logo.width) : none));
  lines.push(fmt(msg("memo_company"), data.company || none));
  lines.push(fmt(msg("memo_color"), colorLabel || none));

  const imageRefs = Object.keys(data.images);
  if (imageRefs.length) {
    lines.push(fmt(msg("memo_images"), imageRefs.length));
    for (const ref of imageRefs) {
      const info = manifest ? manifest.images.find((i) => i.ref === ref) : null;
      const where = info ? sectionLabel(info.section) : "";
      const slides = info && info.slides > 1 ? fmt(msg("memo_slides"), info.slides) : "";
      lines.push(`  - ${where}${slides}`);
    }
  } else {
    lines.push(msg("memo_images_none"));
  }

  const refs = [...new Set([...Object.keys(data.texts), ...Object.keys(data.text_sizes)])];
  if (refs.length) {
    lines.push(msg("memo_texts"));
    for (const ref of refs) {
      const info = manifest ? manifest.texts.find((t) => t.ref === ref) : null;
      const where = info ? sectionLabel(info.section) : "";
      const orig = origTexts[ref] || (info ? info.orig : []);
      const size = data.text_sizes[ref] ? fmt(msg("memo_size"), String(data.text_sizes[ref]).toUpperCase()) : "";
      const next = data.texts[ref] ? data.texts[ref].join(" ／ ") : msg("memo_text_same");
      lines.push(`  - ${where} ／ ${headOf(orig)} → ${next}${size}`);
    }
  } else {
    lines.push(msg("memo_texts_none"));
  }

  lines.push("", msg("memo_foot_demo"), msg("memo_foot_scope"), msg("memo_foot_hearing"));
  return lines.join("\n");
}
